import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table';

import { Empty, Spin } from 'antd';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import IntlMessages from 'util/IntlMessages';

import SortableHeader from './components/SortableHeader';
import {
  ACTIONS_LAYOUT,
  ACTIONS_LAYOUT_CLASSES,
  ALIGN_CLASSES,
  RESPONSIVE_COLUMN_TYPE,
} from './constants';
import { useSortableColumns } from './hooks/useSortableColumns';
import styles from './styles.module.less';
import { useIsMobile, usePersistedTableSort } from '@src/packages/utils';

const formatColumnWidth = width => {
  if (width == null || width === '') {
    return null;
  }
  return typeof width === 'number' ? `${width}px` : width;
};

const wrapColumnContent = (content, width, skipWidthWrap = false) => {
  const w = formatColumnWidth(width);
  if (!w || skipWidthWrap) {
    return content;
  }
  return (
    <div
      className={styles.columnWidthWrap}
      data-column-width-wrap
      style={{ width: w, boxSizing: 'border-box' }}
    >
      {content}
    </div>
  );
};

/**
 * ResponsiveTable component renders a responsive table with optional loading state and empty state.
 *
 * @param {Object[]} dataSource - The data source for the table.
 * @param {Object[]} columns - The columns configuration for the table.
 * @param {string} [className=''] - Additional class names for the table.
 * @param {boolean} [loading=false] - Loading state for the table.
 * @param {string|null} [type=null] - Type of the table for additional styling.
 * @param {boolean} [sortable=false] - Enable sorting globally for all columns with sortConfig.
 * @param {string} [sortStorageKey] - When set, persist/restore sort in sessionStorage across remounts.
 * @param {Object} [defaultSortState=null] - Initial sort state { key, order }. Ignored when sortStorageKey is set.
 * @param {Function} [onSortChange] - Callback when sort changes. Still called when sortStorageKey is set.
 * @param {boolean} [stickyHeader=false] - Pin column headers while scrolling. Requires a constrained
 *   `maxHeight` so `.tableWrapper` becomes the vertical scrollport.
 * @param {string} [wrapperClassName=''] - Extra class names for the table wrapper.
 *
 * @param {Object} columns[].key - Unique key for the column.
 * @param {string|React.ReactNode} columns[].title - Title of the column.
 * @param {string} columns[].dataIndex - Data index for the column.
 * @param {Function} [columns[].render] - Custom render function for the column.
 * @param {any} [columns[].width] - Width of the column (number = px). Wraps header and cell content in a div with this width so text wraps inside it.
 * @param {'left'|'center'|'right'} [columns[].align] - Alignment of the column.
 * @param {boolean} [columns[].sortable] - Enable sorting for this column.
 * @param {Object} [columns[].sortConfig] - Sort configuration { type, path, format }.
 * @param {'actions'} [columns[].columnType] - Optional column role. Use `'actions'` for action button rows on mobile.
 * @param {'start'|'center'|'end'|'between'|'around'} [columns[].actionsLayout='end'] - Flex alignment for action buttons on mobile.
 *
 * @returns {JSX.Element} The rendered responsive table component.
 */

const getActionsLayoutClassName = column => {
  if (column?.columnType !== RESPONSIVE_COLUMN_TYPE.ACTIONS) {
    return null;
  }

  const layout = column?.actionsLayout || ACTIONS_LAYOUT.AROUND;
  return ACTIONS_LAYOUT_CLASSES[layout] || ACTIONS_LAYOUT_CLASSES[ACTIONS_LAYOUT.AROUND];
};

const getColumnClassName = column =>
  classNames(
    ALIGN_CLASSES[column?.contentAlign || column?.align || 'left'],
    column?.columnType === RESPONSIVE_COLUMN_TYPE.ACTIONS && styles.actionsColumn,
    getActionsLayoutClassName(column),
  );

const ResponsiveTable = ({
  dataSource = [],
  columns = [],
  className = '',
  wrapperClassName = '',
  loading = false,
  type = 'light',
  maxHeight = 'none',
  stickyHeader = false,
  onScroll,
  sortable = false,
  sortStorageKey,
  defaultSortState = null,
  onSortChange,
  dataResetKey,
  ...rest
}) => {
  const { isMobile } = useIsMobile();
  const {
    defaultSortState: persistedDefaultSortState,
    onSortChange: persistSortChange,
  } = usePersistedTableSort(sortStorageKey);
  const resolvedDefaultSortState = sortStorageKey
    ? persistedDefaultSortState
    : defaultSortState;
  const handleSortChange = useCallback(
    state => {
      if (sortStorageKey) {
        persistSortChange(state);
      }
      onSortChange?.(state);
    },
    [sortStorageKey, persistSortChange, onSortChange],
  );

  const { sortedData, sortState, handleSort, isColumnSortable } = useSortableColumns(
    dataSource,
    columns,
    {
      sortable,
      defaultSortState: resolvedDefaultSortState,
      onSortChange: sortStorageKey || onSortChange ? handleSortChange : undefined,
    },
  );

  /**
   * Rows from the last settled render. A fetch that momentarily empties
   * `dataSource` (a page being filled batch by batch, a realtime resubscribe)
   * would otherwise blank the table body and swap in the empty state under the
   * spinner, so the list flashes once per fetch. Keeping the previous rows under
   * the spinner is what makes it read as one load instead of several.
   *
   * Only valid while the rows still answer the same question: `dataResetKey`
   * (the caller's filter identity) drops them, so a filter change can never show
   * the previous filter's rows under the new filter's spinner.
   */
  const lastSettledRowsRef = useRef([]);
  useEffect(() => {
    lastSettledRowsRef.current = [];
  }, [dataResetKey]);
  useEffect(() => {
    if (!loading) {
      lastSettledRowsRef.current = sortedData;
    }
  }, [loading, sortedData]);

  const rows = sortedData.length > 0 || !loading ? sortedData : lastSettledRowsRef.current;
  /** Nothing to show and nothing coming: the empty state is the answer. */
  const showEmptyState = rows.length === 0 && !loading;

  const processedColumns = useMemo(() => {
    return columns.map(column => {
      if (!isColumnSortable(column) || isMobile) {
        return column;
      }

      return {
        ...column,
        title: (
          <SortableHeader
            title={column.title}
            columnKey={column.dataIndex || column.key}
            sortState={sortState}
            onSort={handleSort}
          />
        ),
      };
    });
  }, [columns, sortState, handleSort, isColumnSortable, isMobile]);

  const getHeaderCellProps = column => {
    if (!column.onHeaderCell) {
      return {};
    }
    return column.onHeaderCell();
  };

  const typeAux = {
    primary: styles.tablePrimary,
    dark: styles.tableDark,
    light: styles.tableLight,
  };

  const wrapperTypeAux = {
    dark: styles.tableDarkWrapper,
    light: styles.tableLightWrapper,
  };

  const wrapperHeight =
    stickyHeader && maxHeight !== 'none'
      ? typeof maxHeight === 'number'
        ? `${maxHeight}px`
        : maxHeight
      : undefined;

  return (
    <Spin spinning={loading} wrapperClassName={stickyHeader ? styles.stickySpin : undefined}>
      <div
        className={classNames(
          styles.tableWrapper,
          type && wrapperTypeAux[type],
          stickyHeader && styles.stickyHeader,
          wrapperClassName,
        )}
        style={{ maxHeight, height: wrapperHeight }}
        onScroll={onScroll}
        {...rest}
      >
        <Table
          key={isMobile ? 'm' : 'd'}
          className={classNames(styles.tableContainer, type && typeAux[type], className)}
        >
          <Thead>
            <Tr>
              {processedColumns.map(column => (
                <Th
                  style={{ width: column?.width, ...column?.styles }}
                  className={classNames(
                    ALIGN_CLASSES[column?.align || 'left'],
                    column?.columnType === RESPONSIVE_COLUMN_TYPE.ACTIONS && styles.actionsColumn,
                    getActionsLayoutClassName(column),
                  )}
                  key={column.key}
                  {...getHeaderCellProps(column)}
                >
                  {wrapColumnContent(column?.title, column?.width, isMobile)}
                </Th>
              ))}
            </Tr>
          </Thead>
          {!showEmptyState ? (
            <Tbody>
              {rows.map(data => (
                <Tr key={data.key || data.id}>
                  {processedColumns.map(column => (
                    <Td className={getColumnClassName(column)} key={column.key}>
                      {wrapColumnContent(
                        column.render ? column.render(data) : data[column.dataIndex],
                        column?.width,
                        isMobile,
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          ) : (
            <Tbody>
              <tr>
                <td colSpan={processedColumns.length}>
                  <Empty description={<IntlMessages id="general.empty.state" />} />
                </td>
              </tr>
            </Tbody>
          )}
        </Table>
      </div>
    </Spin>
  );
};

ResponsiveTable.propTypes = {
  dataSource: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      dataIndex: PropTypes.string,
      render: PropTypes.func,
      width: PropTypes.any,
      align: PropTypes.oneOf(['left', 'center', 'right']),
      contentAlign: PropTypes.oneOf(['left', 'center', 'right']),
      columnType: PropTypes.oneOf([RESPONSIVE_COLUMN_TYPE.ACTIONS]),
      actionsLayout: PropTypes.oneOf(Object.values(ACTIONS_LAYOUT)),
      styles: PropTypes.object,
      sortable: PropTypes.bool,
      sortConfig: PropTypes.shape({
        type: PropTypes.string.isRequired,
        path: PropTypes.string,
        format: PropTypes.string,
      }),
      onHeaderCell: PropTypes.func,
    }),
  ).isRequired,
  className: PropTypes.string,
  wrapperClassName: PropTypes.string,
  type: PropTypes.oneOf(['primary', 'dark', 'light']),
  loading: PropTypes.bool,
  /** Identity of the current dataset (e.g. the active filters); changing it drops the rows kept under the spinner. */
  dataResetKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  stickyHeader: PropTypes.bool,
  sortable: PropTypes.bool,
  sortStorageKey: PropTypes.string,
  defaultSortState: PropTypes.shape({
    key: PropTypes.string,
    order: PropTypes.oneOf(['asc', 'desc']),
  }),
  onSortChange: PropTypes.func,
};

export default React.memo(ResponsiveTable);
